import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'
import { formatPlanDateRange, isWithinDays } from '../../../../../../../utils/utils'
import { ALLOCATION_PLANS_THEME, REBALANCE_PLANS_THEME } from './riskProfileHeaderTheme'

const PLAN_UPDATED_BANNER_VISIBILITY_DAYS = 15

export enum ListPlansPlanUpdatedBannerType {
  ALLOCATION = 'ALLOCATION',
  REBALANCE = 'REBALANCE',
  SIP = 'SIP'
}

type ListPlansPlanUpdatedBannerProps = {
  bannerType: ListPlansPlanUpdatedBannerType
  planSnapshotDate: number
  planDurationInMonths?: number | null
  onSeeWhatChangedClick?: () => void
}

const ALLOCATION_PLAN_UPDATED_BANNER = {
  background:
    'linear-gradient(90deg, rgba(252, 241, 237, 0.80) -86.12%, rgba(237, 206, 196, 0.80) 157.91%)',
  link: '#B85230',
  border: '#EDCEC4',
  titleClassName: ALLOCATION_PLANS_THEME.primaryDarkTextClassName,
  iconColor: ALLOCATION_PLANS_THEME.primaryDark
}

const PLAN_UPDATED_BANNER_CHROME = {
  [ListPlansPlanUpdatedBannerType.ALLOCATION]: ALLOCATION_PLAN_UPDATED_BANNER,
  [ListPlansPlanUpdatedBannerType.SIP]: ALLOCATION_PLAN_UPDATED_BANNER,
  [ListPlansPlanUpdatedBannerType.REBALANCE]: {
    background:
      'linear-gradient(90deg, rgba(255, 247, 241, 0.80) -86.12%, rgba(244, 218, 198, 0.80) 157.91%)',
    link: '#CC6E30',
    border: '#F4DAC6',
    titleClassName: REBALANCE_PLANS_THEME.primaryDarkTextClassName,
    iconColor: REBALANCE_PLANS_THEME.primaryDark
  }
} as const

export function ListPlansPlanUpdatedBanner({
  bannerType,
  planSnapshotDate,
  planDurationInMonths,
  onSeeWhatChangedClick
}: ListPlansPlanUpdatedBannerProps) {
  const bannerChrome = PLAN_UPDATED_BANNER_CHROME[bannerType]
  const planMonthRangeLabel = formatPlanDateRange(planSnapshotDate, planDurationInMonths ?? 1)

  return (
    <div className="px-5 pt-4 pb-5">
      <button
        type="button"
        className="relative z-[2] flex w-full items-center rounded-[16px] border px-5 py-[14px] text-left"
        style={{
          background: bannerChrome.background,
          borderColor: bannerChrome.border
        }}
        onClick={onSeeWhatChangedClick}
      >
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <PlanUpdatedBannerIcon strokeColor={bannerChrome.iconColor} />
          <div className="flex min-w-0 flex-col gap-[4px]">
            <div className={`text-[14px] font-[500] leading-[140%] ${bannerChrome.titleClassName}`}>
              Plan updated for {planMonthRangeLabel}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[13px] font-[500] leading-[140%]" style={{ color: bannerChrome.link }}>
                See what&apos;s changed
              </span>
              <PlanUpdatedBannerChevronIcon fillColor={bannerChrome.link} />
            </div>
          </div>
        </div>
      </button>
    </div>
  )
}

export function shouldShowPlanUpdatedBanner(
  planCount?: number | null,
  planSnapshotDate?: number | null
): boolean {
  return (planCount ?? 0) > 1 && isWithinDays(planSnapshotDate ?? undefined, PLAN_UPDATED_BANNER_VISIBILITY_DAYS)
}

export function PlanUpdatedBannerIcon({ strokeColor }: { strokeColor: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" className="shrink-0" aria-hidden>
      <path
        d="M5.92634 3.33887H2.30134C2.02665 3.33887 1.76321 3.44799 1.56898 3.64222C1.37474 3.83646 1.26562 4.09989 1.26562 4.37458V13.696C1.26562 13.9707 1.37474 14.2342 1.56898 14.4284C1.76321 14.6226 2.02665 14.7317 2.30134 14.7317H12.6585C13.2305 14.7317 13.6942 14.268 13.6942 13.696V8.51744"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.26562 6.44629H7.47991"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.7873 2.67038L11.3157 1.38729C11.3455 1.31485 11.4161 1.26758 11.4944 1.26758C11.5727 1.26758 11.6434 1.31485 11.6732 1.38729L12.2015 2.67038C12.4119 3.18116 12.8174 3.58677 13.3283 3.79709L14.6113 4.32543C14.6838 4.35525 14.731 4.42584 14.731 4.50419C14.731 4.58253 14.6838 4.65312 14.6113 4.68294L13.3283 5.21128C12.8174 5.4216 12.4119 5.82721 12.2015 6.33799L11.6732 7.62108C11.6434 7.69351 11.5727 7.74079 11.4944 7.74079C11.4161 7.74079 11.3455 7.69351 11.3157 7.62108L10.7873 6.33799C10.577 5.82721 10.1714 5.4216 9.66061 5.21128L8.37752 4.68294C8.30509 4.65312 8.25781 4.58253 8.25781 4.50419C8.25781 4.42584 8.30509 4.35525 8.37752 4.32543L9.66061 3.79709C10.1714 3.58677 10.577 3.18116 10.7873 2.67038Z"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PlanUpdatedBannerChevronIcon({ fillColor }: { fillColor: string }) {
  return (
    <RenderHTML
      innerHTMLText={`<svg xmlns="http://www.w3.org/2000/svg" width="5" height="6" viewBox="0 0 5 6" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M0.450129 5.95431C0.177654 5.86144 -1.56198e-08 5.64266 -2.62274e-08 5.39999L-2.36041e-07 0.600014C-2.46649e-07 0.357339 0.177654 0.138556 0.450129 0.0456881C0.722589 -0.0471797 1.03622 0.00415654 1.24476 0.175756L4.1615 2.57574C4.44617 2.81005 4.44617 3.18995 4.1615 3.42426L1.24476 5.82424C1.03622 5.99584 0.722589 6.04718 0.450129 5.95431Z" fill="${fillColor}"/>
</svg>`}
    />
  )
}
